
import React, { useRef, useEffect } from "react";
import ChatMessage from "./chat/ChatMessage";
import ChatInput from "./chat/ChatInput";
import TargetButtons from "./chat/TargetButtons";
import TypingIndicator from "./chat/TypingIndicator";
import TargetGenerationIndicator from "./chat/TargetGenerationIndicator";
import { useChat } from "@/context/ChatContext";
import { Spinner } from "@/components/ui/spinner";
import { useLocation } from "react-router-dom";
import Sidebar from "./layout/Sidebar";
import MobileSidebar from "./layout/MobileSidebar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTargetGeneration } from "@/context/hooks/useTargetGeneration";
import Footer from "./layout/Footer";

const RemoteVizApp: React.FC = () => {
  const { 
    messages, 
    isLoading, 
    sendMessage,
    isNewSession
  } = useChat();
  
  const { isGenerating, generatingType } = useTargetGeneration();
  const location = useLocation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const isMainPage = location.pathname === "/";

  useEffect(() => {
    console.log('🔄 RemoteVizApp: useEffect triggered - messages length:', messages.length, 'isLoading:', isLoading);
    
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        console.log('📜 RemoteVizApp: Scrolling to bottom');
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        console.log('❌ RemoteVizApp: messagesEndRef is null, cannot scroll');
      }
    };

    // Check if the last message contains complex content that needs loading time
    const lastMessage = messages[messages.length - 1];
    console.log('📝 RemoteVizApp: Last message:', lastMessage?.content?.substring(0, 100) + '...');
    
    const hasComplexContent = lastMessage && (
      lastMessage.content.includes('![') || // Images
      lastMessage.content.includes('maps.google') || // Maps
      lastMessage.content.includes('street view') ||
      lastMessage.content.includes("Here's your") // Target reveals
    );

    console.log('🔍 RemoteVizApp: Has complex content:', hasComplexContent);

    if (hasComplexContent) {
      // For target reveals and complex content, add a delay to allow content to load
      console.log('⏰ RemoteVizApp: Scheduling delayed scroll for complex content');
      setTimeout(() => {
        console.log('⏰ RemoteVizApp: Executing delayed scroll');
        scrollToBottom();
      }, 1000); // Increased delay to 1 second
    } else {
      // For regular messages, scroll immediately
      console.log('⚡ RemoteVizApp: Scrolling immediately for regular content');
      scrollToBottom();
    }
  }, [messages, isLoading]);

  const handleTargetButtonClick = (buttonLabel: string) => {
    console.log('🔄 RemoteVizApp: Target button clicked:', buttonLabel);
    sendMessage(buttonLabel);
  };

  const getTypingIndicatorSide = (): "left" | "right" => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.type === "user") {
      return "left";
    }
    return "left";
  };

  return (
    <div className="flex w-full h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col relative min-w-0">
        {/* Mobile Header with Hamburger Menu */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-border bg-background">
          <MobileSidebar />
          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-3">
            <svg
              width="28"
              height="28"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="16"
                cy="16"
                r="14.5"
                stroke="currentColor"
                strokeWidth="3"
                className="text-foreground"
              ></circle>
              <mask id="path-2-inside-1_102_2292" fill="white">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7 16.195C8.79648 13.0894 12.1542 11 16 11C19.8458 11 23.2035 13.0894 25 16.195C23.2035 19.3006 19.8458 21.39 16 21.39C12.1542 21.39 8.79648 19.3006 7 16.195Z"
                ></path>
              </mask>
              <path
                d="M7 16.195L5.26879 15.1935L4.68948 16.195L5.26879 17.1965L7 16.195ZM25 16.195L26.7312 17.1965L27.3105 16.195L26.7312 15.1936L25 16.195ZM8.73121 17.1965C10.1849 14.6835 12.897 13 16 13V9C11.4114 9 7.40806 11.4954 5.26879 15.1935L8.73121 17.1965ZM16 13C19.103 13 21.8151 14.6835 23.2688 17.1965L26.7312 15.1936C24.5919 11.4954 20.5886 9 16 9V13ZM23.2688 15.1936C21.8151 17.7065 19.103 19.39 16 19.39V23.39C20.5886 23.39 24.5919 20.8946 26.7312 17.1965L23.2688 15.1936ZM16 19.39C12.897 19.39 10.1849 17.7065 8.73121 15.1935L5.26879 17.1965C7.40806 20.8946 11.4114 23.39 16 23.39V19.39Z"
                fill="currentColor"
                mask="url(#path-2-inside-1_102_2292)"
                className="text-foreground"
              ></path>
              <circle
                cx="16"
                cy="16.1951"
                r="3"
                stroke="currentColor"
                strokeWidth="2"
                className="text-foreground"
              ></circle>
              <path
                d="M3.87564 23L16 2L28.1244 23H3.87564Z"
                stroke="currentColor"
                strokeWidth="2"
                className="text-foreground"
              ></path>
            </svg>
            <div className="text-foreground text-xl font-medium">remoteviz</div>
          </div>
          <div className="w-10"></div>
        </div>

        {isMainPage && (
          <>
            <ScrollArea className="flex-1 px-2 sm:px-4 md:px-8 lg:px-16 xl:px-24 py-6">
              <div className="flex flex-col gap-6 max-w-4xl mx-auto w-full">
                {/* Show new session confirmation message only for manually created sessions */}
                {isNewSession && (
                  <div className="py-8">
                    <div className="text-base font-normal text-foreground mb-4 text-left">
                      A new session has been created—ready when you are.
                    </div>
                    <div className="text-left text-muted-foreground space-y-4">
                      <div>
                        <span className="font-semibold text-[#734E85]">🎯 Generate a new target to begin your next remote viewing attempt.</span>
                      </div>
                      <div>
                        <span className="font-semibold text-[#734E85]">🙋 Need help? Just type "help" anytime.</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Display all chat messages naturally */}
                {messages.map((message, index) => (
                  <ChatMessage
                    key={message.id || index}
                    content={message.content}
                    type={message.type}
                    timestamp={message.timestamp}
                    files={message.files}
                  />
                ))}
                
                {isLoading && (() => {
                  // Use the stable target generation state
                  if (isGenerating && generatingType) {
                    return <TargetGenerationIndicator targetType={generatingType} />;
                  }
                  return <TypingIndicator side={getTypingIndicatorSide()} />;
                })()}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
            <div>
              <div className="px-2 sm:px-4 md:px-8 lg:px-16 xl:px-24">
                <div className="max-w-4xl mx-auto w-full">
                  <ChatInput onSendMessage={sendMessage} isLoading={isLoading} />
                  <div className="flex justify-center pb-6">
                    <TargetButtons onButtonClick={handleTargetButtonClick} />
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </>
        )}
        {!isMainPage && (
          <div className="flex-1 flex items-center justify-center">
            <h2 className="text-xl text-muted-foreground">Select a page from the sidebar</h2>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemoteVizApp;
